/**
 * 토큰 관리 유틸리티 클래스
 * HttpOnly 쿠키 기반 토큰 저장 및 관리
 */
import JWTValidator from './jwtValidator';

export class TokenManager {
    private static readonly ACCESS_TOKEN_KEY = 'access_token';
    private static readonly REFRESH_TOKEN_KEY = 'refresh_token';
    private static readonly ADMIN_INFO_KEY = 'admin_info';

    // 쿠키 설정 옵션 (fallback용 - localStorage가 실패할 경우에만 사용)
    private static readonly COOKIE_OPTIONS = {
        httpOnly: false, // 클라이언트에서 접근 가능 (보안: 서버에서 HttpOnly 설정 권장)
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'lax' as const,
        path: '/',
    };

    /**
     * 쿠키 설정
     */
    private static setCookie(name: string, value: string, days: number = 7): void {
        try {
            const expires = new Date();
            expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));

            // 더 간단한 쿠키 설정 방식
            let cookieString = `${name}=${value}; expires=${expires.toUTCString()}; path=/`;

            // SameSite 설정 (브라우저 호환성을 위해 조건부)
            if (this.COOKIE_OPTIONS.sameSite) {
                cookieString += `; SameSite=${this.COOKIE_OPTIONS.sameSite}`;
            }

            // Secure 설정 (HTTPS에서만)
            if (this.COOKIE_OPTIONS.secure && window.location.protocol === 'https:') {
                cookieString += '; Secure';
            }

            console.log('쿠키 설정:', {
                name,
                value_preview: value.substring(0, 20) + '...',
                days,
                expires: expires.toUTCString(),
                cookieString: cookieString.substring(0, 100) + '...'
            });

            document.cookie = cookieString;

            // 쿠키 설정 후 확인 (약간의 지연 후)
            setTimeout(() => {
                const savedCookie = this.getCookie(name);
                console.log('쿠키 설정 확인:', {
                    name,
                    saved: !!savedCookie,
                    saved_preview: savedCookie ? savedCookie.substring(0, 20) + '...' : null,
                    all_cookies: document.cookie.substring(0, 200) + '...'
                });
            }, 100);
        } catch (error) {
            console.error('쿠키 설정 실패:', error);
        }
    }

    /**
     * 쿠키에서 값 가져오기
     */
    private static getCookie(name: string): string | null {
        try {
            const nameEQ = name + "=";
            const ca = document.cookie.split(';');

            for (let i = 0; i < ca.length; i++) {
                let c = ca[i];
                while (c.charAt(0) === ' ') c = c.substring(1, c.length);
                if (c.indexOf(nameEQ) === 0) {
                    const value = c.substring(nameEQ.length, c.length);
                    return value;
                }
            }
            return null;
        } catch (error) {
            console.warn('쿠키 읽기 실패:', error);
            return null;
        }
    }

    /**
     * 쿠키 삭제
     */
    private static deleteCookie(name: string): void {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${this.COOKIE_OPTIONS.path};`;
    }

    /**
     * Access Token 저장
     * localStorage를 주 저장소로 사용하고, 쿠키는 fallback으로만 사용
     */
    static setAccessToken(token: string): void {
        console.log('TokenManager.setAccessToken 호출:', token.substring(0, 20) + '...');

        try {
            // localStorage에 저장 (주 저장소)
            localStorage.setItem(this.ACCESS_TOKEN_KEY, token);

            // 저장 확인 (즉시 확인)
            const savedToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);
            if (!savedToken || savedToken !== token) {
                console.error('Access Token localStorage 저장 실패 - 저장된 값이 일치하지 않음');
                throw new Error('Access Token localStorage 저장 실패');
            }
            console.log('Access Token localStorage 저장 완료 및 확인됨');

            // 쿠키는 fallback으로만 저장 (localStorage가 실패할 경우를 대비)
            // 보안: 쿠키는 XSS 공격에 취약하므로 localStorage를 우선 사용
            try {
                const tokenInfo = JWTValidator.getTokenInfo(token);
                let cookieExpiryDays = 1; // 기본값 1일

                if (tokenInfo.isValid && tokenInfo.expiresIn !== 'N/A') {
                    const expiresInHours = this.parseExpirationTime(tokenInfo.expiresIn);
                    cookieExpiryDays = Math.max(1 / 24, Math.min(7, expiresInHours / 24));
                }

                this.setCookie(this.ACCESS_TOKEN_KEY, token, cookieExpiryDays);
                console.log('Access Token 쿠키 fallback 저장 완료 (만료일:', cookieExpiryDays, '일)');
            } catch (cookieError) {
                // 쿠키 저장 실패는 치명적이지 않음 (localStorage가 주 저장소)
                console.warn('Access Token 쿠키 저장 실패 (무시됨 - localStorage 사용):', cookieError);
            }
        } catch (error) {
            console.error('Access Token localStorage 저장 실패:', error);

            // localStorage 실패 시 쿠키로 fallback 시도
            try {
                const tokenInfo = JWTValidator.getTokenInfo(token);
                let cookieExpiryDays = 1;
                if (tokenInfo.isValid && tokenInfo.expiresIn !== 'N/A') {
                    const expiresInHours = this.parseExpirationTime(tokenInfo.expiresIn);
                    cookieExpiryDays = Math.max(1 / 24, Math.min(7, expiresInHours / 24));
                }
                this.setCookie(this.ACCESS_TOKEN_KEY, token, cookieExpiryDays);
                console.warn('Access Token 쿠키만 저장 완료 (localStorage 실패 - fallback)');
            } catch (cookieError) {
                console.error('쿠키 저장도 실패:', cookieError);
                throw new Error('토큰 저장에 실패했습니다. 브라우저 설정을 확인해주세요.');
            }
        }
    }

    /**
     * 만료 시간 문자열을 시간 단위로 파싱
     */
    private static parseExpirationTime(expiresIn: string): number {
        if (expiresIn === 'N/A' || expiresIn === '만료됨') {
            return 1; // 1시간 기본값
        }

        // "X시간 Y분" 형태 파싱
        const hourMatch = expiresIn.match(/(\d+)시간/);
        const minuteMatch = expiresIn.match(/(\d+)분/);

        let hours = 0;
        if (hourMatch) hours += parseInt(hourMatch[1]);
        if (minuteMatch) hours += parseInt(minuteMatch[1]) / 60;

        return Math.max(1, hours); // 최소 1시간
    }

    /**
     * Refresh Token 저장
     * localStorage를 주 저장소로 사용하고, 쿠키는 fallback으로만 사용
     */
    static setRefreshToken(token: string): void {
        console.log('TokenManager.setRefreshToken 호출:', token.substring(0, 20) + '...');

        try {
            // localStorage에 저장 (주 저장소)
            localStorage.setItem(this.REFRESH_TOKEN_KEY, token);

            // 저장 확인 (즉시 확인)
            const savedToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
            if (!savedToken || savedToken !== token) {
                console.error('Refresh Token localStorage 저장 실패 - 저장된 값이 일치하지 않음');
                throw new Error('Refresh Token localStorage 저장 실패');
            }
            console.log('Refresh Token localStorage 저장 완료 및 확인됨');

            // 쿠키는 fallback으로만 저장
            try {
                this.setCookie(this.REFRESH_TOKEN_KEY, token, 7);
                console.log('Refresh Token 쿠키 fallback 저장 완료');
            } catch (cookieError) {
                // 쿠키 저장 실패는 치명적이지 않음
                console.warn('Refresh Token 쿠키 저장 실패 (무시됨 - localStorage 사용):', cookieError);
            }
        } catch (error) {
            console.error('Refresh Token localStorage 저장 실패:', error);

            // localStorage 실패 시 쿠키로 fallback 시도
            try {
                this.setCookie(this.REFRESH_TOKEN_KEY, token, 7);
                console.warn('Refresh Token 쿠키만 저장 완료 (localStorage 실패 - fallback)');
            } catch (cookieError) {
                console.error('쿠키 저장도 실패:', cookieError);
                throw new Error('토큰 저장에 실패했습니다. 브라우저 설정을 확인해주세요.');
            }
        }
    }

    /**
     * Access Token 가져오기
     * localStorage를 우선 사용하고, 없으면 쿠키에서 fallback
     */
    static getAccessToken(): string | null {
        let localToken: string | null = null;
        let cookieToken: string | null = null;

        // localStorage에서 먼저 확인 (주 저장소)
        try {
            localToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);
        } catch (error) {
            console.warn('localStorage에서 Access Token 읽기 실패:', error);
        }

        // localStorage에 없으면 쿠키에서 fallback
        if (!localToken) {
            cookieToken = this.getCookie(this.ACCESS_TOKEN_KEY);
        }

        const token = localToken || cookieToken;
        console.log('TokenManager.getAccessToken:', {
            local_token: localToken ? localToken.substring(0, 20) + '...' : null,
            cookie_token: cookieToken ? cookieToken.substring(0, 20) + '...' : null,
            final_token: token ? token.substring(0, 20) + '...' : null,
            source: localToken ? 'localStorage' : (cookieToken ? 'cookie' : 'none')
        });
        return token;
    }

    /**
     * Refresh Token 가져오기
     * localStorage를 우선 사용하고, 없으면 쿠키에서 fallback
     */
    static getRefreshToken(): string | null {
        let localToken: string | null = null;
        let cookieToken: string | null = null;

        // localStorage에서 먼저 확인 (주 저장소)
        try {
            localToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
        } catch (error) {
            console.warn('localStorage에서 Refresh Token 읽기 실패:', error);
        }

        // localStorage에 없으면 쿠키에서 fallback
        if (!localToken) {
            cookieToken = this.getCookie(this.REFRESH_TOKEN_KEY);
        }

        const token = localToken || cookieToken;

        console.log('TokenManager.getRefreshToken:', {
            local_token: localToken ? localToken.substring(0, 20) + '...' : null,
            cookie_token: cookieToken ? cookieToken.substring(0, 20) + '...' : null,
            final_token: token ? token.substring(0, 20) + '...' : null,
            source: localToken ? 'localStorage' : (cookieToken ? 'cookie' : 'none')
        });

        return token;
    }

    /**
     * 관리자 정보 저장 (localStorage 사용 - 민감하지 않은 정보)
     */
    static setAdminInfo(adminInfo: any): void {
        localStorage.setItem(this.ADMIN_INFO_KEY, JSON.stringify(adminInfo));
    }

    /**
     * 관리자 정보 가져오기
     */
    static getAdminInfo(): any {
        const adminInfo = localStorage.getItem(this.ADMIN_INFO_KEY);
        return adminInfo ? JSON.parse(adminInfo) : null;
    }

    /**
     * 모든 토큰 및 정보 삭제
     */
    static clearAll(): void {
        console.log('TokenManager.clearAll 시작');

        // 쿠키 삭제
        this.deleteCookie(this.ACCESS_TOKEN_KEY);
        this.deleteCookie(this.REFRESH_TOKEN_KEY);

        // localStorage 삭제
        localStorage.removeItem(this.ACCESS_TOKEN_KEY);
        localStorage.removeItem(this.REFRESH_TOKEN_KEY);
        localStorage.removeItem(this.ADMIN_INFO_KEY);

        // 삭제 확인
        const accessToken = this.getAccessToken();
        const refreshToken = this.getRefreshToken();
        const adminInfo = this.getAdminInfo();

        console.log('TokenManager.clearAll 완료:', {
            accessToken_cleared: !accessToken,
            refreshToken_cleared: !refreshToken,
            adminInfo_cleared: !adminInfo
        });
    }

    /**
     * JWT 토큰 만료 시간 검증
     */
    static isTokenExpired(token: string | null): boolean {
        if (!token) return true;
        return JWTValidator.isTokenExpired(token);
    }

    /**
     * 토큰 만료까지 남은 시간 (초)
     */
    static getTokenExpirationTime(token: string | null): number {
        if (!token) return 0;
        return JWTValidator.getTokenExpirationTime(token);
    }

    /**
     * 토큰이 곧 만료되는지 확인 (5분 이내)
     */
    static isTokenExpiringSoon(token: string | null): boolean {
        if (!token) return true;
        return JWTValidator.isTokenExpiringSoon(token);
    }

    /**
     * 토큰 갱신이 필요한지 확인
     */
    static shouldRefreshToken(token: string | null): boolean {
        if (!token) return true;
        return JWTValidator.shouldRefreshToken(token);
    }

    /**
     * 토큰 정보 가져오기
     */
    static getTokenInfo(token: string | null): any {
        if (!token) return null;
        return JWTValidator.getTokenInfo(token);
    }
}

export default TokenManager;
